import { ReactNode, useState } from "react";
import {
  SkippedSyncTable,
  syncSchemaTables,
  SyncSchemaTablesResponse,
} from "admin/api/syncSchemaTables";
import { ChevronRight, CircleCheck, Loader2, TriangleAlert, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";

/** What syncing a selected table will do to it. */
export const SyncAction = {
  REGISTER: "register",
  REFRESH: "refresh",
  REMOVE: "remove",
} as const;
export type SyncAction = (typeof SyncAction)[keyof typeof SyncAction];

export interface SyncItem {
  /** Unique across columns: the same name can appear in two of them as a case variant. */
  key: string;
  action: SyncAction;
  /** Bare table name in the schema's own exact case — what the API is sent. */
  table: string;
  fqn: string;
  /** Set on a register whose platform entry differs from Snowflake only in case. */
  caseMismatch?: boolean;
}

interface TableSyncDialogProps {
  schema: string;
  items: SyncItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Drops one table from the pending selection, from the review list. */
  onDeselect: (key: string) => void;
  /** Called once the outcome has been read: refresh the diff and clear the selection. */
  onSyncComplete: () => void;
}

const ACTION_HEADING: Record<SyncAction, string> = {
  [SyncAction.REGISTER]: "Register in Manifold",
  [SyncAction.REFRESH]: "Refresh metadata",
  [SyncAction.REMOVE]: "Remove from Manifold",
};

const ACTION_ORDER: SyncAction[] = [SyncAction.REGISTER, SyncAction.REFRESH, SyncAction.REMOVE];

// Wraps rather than truncates: a table name is the whole identity of the row, and
// these run long enough (JUMP_CELLPAINTING_..._FULL_UNPIVOTED) that an ellipsis
// would hide which table you are about to change. min-w-0 lets it shrink to wrap
// instead of forcing the dialog to scroll sideways.
const TableName = ({ children }: { children: ReactNode }) => (
  <span className="min-w-0 flex-1 font-mono text-xs break-all">{children}</span>
);

/**
 * Above this many tables a group collapses by default: a wall of monospace names
 * stops being decision-support and just buries the buttons. The heading still
 * carries the count, so the dialog answers "what am I about to do" while closed.
 */
const COLLAPSE_ABOVE = 10;

interface DisclosureProps {
  /** Rendered inside the trigger, beside the chevron. */
  heading: ReactNode;
  defaultOpen: boolean;
  children: ReactNode;
}

const Disclosure = ({ heading, defaultOpen, children }: DisclosureProps) => (
  <Collapsible defaultOpen={defaultOpen}>
    <CollapsibleTrigger asChild>
      <button type="button" className="group flex w-full items-center gap-1.5 text-left">
        <ChevronRight className="size-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
        {heading}
      </button>
    </CollapsibleTrigger>
    <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
  </Collapsible>
);

interface ReviewGroupProps {
  action: SyncAction;
  items: SyncItem[];
  onDeselect: (key: string) => void;
}

const ReviewGroup = ({ action, items, onDeselect }: ReviewGroupProps) => {
  if (items.length === 0) {
    return null;
  }
  const destructive = action === SyncAction.REMOVE;

  return (
    <div className="space-y-1">
      <Disclosure
        // Removals are the half this dialog exists to guard, so they stay open
        // however many there are; the other groups fold away once long.
        defaultOpen={destructive || items.length <= COLLAPSE_ABOVE}
        heading={
          <span
            className={cn(
              "flex items-center gap-1.5 text-xs font-semibold",
              destructive ? "text-destructive" : "text-foreground"
            )}
          >
            {destructive && <TriangleAlert className="size-3.5 shrink-0" />}
            {ACTION_HEADING[action]} ({items.length})
          </span>
        }
      >
        <ul className="divide-y rounded-md border">
          {items.map((item) => (
            <li key={item.key} className="flex items-start gap-2 px-2 py-1">
              <TableName>{item.table}</TableName>
              {item.caseMismatch && (
                <span className="shrink-0 text-xs text-muted-foreground">case mismatch</span>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto size-5 shrink-0 text-muted-foreground"
                aria-label={`Remove ${item.table} from selection`}
                onClick={() => onDeselect(item.key)}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      </Disclosure>
      {/* Outside the disclosure: the consequence has to be readable even collapsed. */}
      {destructive && (
        <p className="text-xs text-muted-foreground">
          These stop appearing in search and dataset listings.
        </p>
      )}
    </div>
  );
};

interface OutcomeGroupProps {
  title: string;
  names: string[];
  tone: "success" | "failure";
  children?: ReactNode;
}

const OutcomeGroup = ({ title, names, tone, children }: OutcomeGroupProps) => {
  if (names.length === 0 && !children) {
    return null;
  }

  return (
    <div className="space-y-1">
      <p
        className={cn(
          "flex items-center gap-1.5 text-xs font-semibold",
          tone === "success" ? "text-green-700" : "text-destructive"
        )}
      >
        {tone === "success" ? (
          <CircleCheck className="size-3.5 shrink-0" />
        ) : (
          <TriangleAlert className="size-3.5 shrink-0" />
        )}
        {title}
      </p>
      {/* wrap-anywhere, not break-all: breaks a single over-long table name only
          when it would otherwise overflow, leaving the comma-separated list readable. */}
      {names.length > 0 && (
        <p className="font-mono text-xs wrap-anywhere text-muted-foreground">{names.join(", ")}</p>
      )}
      {children}
    </div>
  );
};

const FailureList = ({ skips }: { skips: SkippedSyncTable[] }) => (
  <ul className="space-y-1">
    {skips.map((skip) => (
      <li key={`${skip.table}:${skip.reason}`} className="text-xs break-words">
        <span className="font-mono font-medium break-all">{skip.table}</span>
        <span className="text-muted-foreground"> — {skip.reason}</span>
      </li>
    ))}
  </ul>
);

/**
 * Reviews a pending selection, syncs it, then reports the per-table outcome in
 * place. Doubles as the "inspect the selection" surface: it can be opened before
 * committing, and each listed table can be dropped from the selection here.
 */
const TableSyncDialog = ({
  schema,
  items,
  open,
  onOpenChange,
  onDeselect,
  onSyncComplete,
}: TableSyncDialogProps) => {
  const [result, setResult] = useState<SyncSchemaTablesResponse | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = items.filter((item) => item.action !== SyncAction.REMOVE).map((item) => item.table);
  const remove = items
    .filter((item) => item.action === SyncAction.REMOVE)
    .map((item) => item.table);
  const caseMismatches = items.filter((item) => item.caseMismatch);

  const handleSync = async () => {
    // The endpoint treats a name in both lists as a self-contradicting request and
    // performs neither. Selecting a case variant in two columns is the only way to
    // reach that, so catch it here rather than as an opaque per-table skip.
    const conflict = add.find((table) => remove.includes(table));
    if (conflict) {
      setError(
        `"${conflict}" is selected both to register and to remove. Deselect it in one of the columns.`
      );
      return;
    }

    setIsSyncing(true);
    setError(null);
    try {
      setResult(await syncSchemaTables(schema, { add, remove }));
    } catch (e) {
      setError(isApiError(e) ? getApiErrorMessage(e) : "The sync request failed.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Reset for the next selection, and only tell the page a sync happened if one did.
  const handleClose = () => {
    const synced = result !== null;
    setResult(null);
    setError(null);
    onOpenChange(false);
    if (synced) {
      onSyncComplete();
    }
  };

  const failures = result ? [...result.skipped_registrations, ...result.skipped_soft_deletes] : [];

  return (
    <AlertDialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())}>
      {/* Flex rather than the base grid so only the body scrolls: with a long
          selection the title and the Cancel/Sync buttons must stay put. */}
      <AlertDialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-2xl">
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle>
            {result ? "Sync complete" : `Sync ${items.length} tables in ${schema}?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {result
              ? `Results for ${schema}.`
              : "Review what will change before applying it. Remove anything you didn't mean to select."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {result ? (
            <div className="space-y-3">
              <OutcomeGroup
                title={`Registered (${result.registered.length})`}
                names={result.registered}
                tone="success"
              />
              <OutcomeGroup
                title={`Removed (${result.soft_deleted.length})`}
                names={result.soft_deleted}
                tone="success"
              />
              {failures.length > 0 && (
                <Disclosure
                  // Always open: the reasons are the whole point of the results view.
                  defaultOpen
                  heading={
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-destructive">
                      <TriangleAlert className="size-3.5 shrink-0" />
                      Failed ({failures.length})
                    </span>
                  }
                >
                  <FailureList skips={failures} />
                </Disclosure>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing is selected.</p>
              ) : (
                ACTION_ORDER.map((action) => (
                  <ReviewGroup
                    key={action}
                    action={action}
                    items={items.filter((item) => item.action === action)}
                    onDeselect={onDeselect}
                  />
                ))
              )}
              {caseMismatches.length > 0 && (
                <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
                  {caseMismatches.length === 1
                    ? `1 selection is a case mismatch. Its existing entry (${caseMismatches[0].fqn}) will appear under "Only in Manifold" after this sync, to remove there.`
                    : `${caseMismatches.length} selections are case mismatches. Their existing entries will appear under "Only in Manifold" after this sync, to remove there.`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Pinned beside the footer, not in the scroll area: it reports on the
            button the admin just pressed and has to be visible from there. */}
        {error && <p className="shrink-0 text-sm text-destructive">{error}</p>}

        <AlertDialogFooter className="shrink-0">
          {result ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSyncing}>
                Cancel
              </Button>
              <Button onClick={handleSync} disabled={isSyncing || items.length === 0}>
                {isSyncing && <Loader2 className="mr-2 size-4 animate-spin" />}
                Sync {items.length} tables
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default TableSyncDialog;
